/**
 * Observer Call Queue Component
 * - Daily call queue with Start Call button
 * - Call timer with 4-minute nudge
 * - Mandatory post-call notes form
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Phone, PhoneCall, PhoneOff, Clock, User, Baby, 
  CheckCircle, AlertCircle, Lock, Play, Square,
  Loader2, Star, MessageSquare, RefreshCw, X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 4 minutes in seconds for nudge
const NUDGE_TIME = 4 * 60;

const ObserverCallQueue = ({ observerId }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({});
  const [incompleteCall, setIncompleteCall] = useState(null);
  const [todayDate, setTodayDate] = useState('');
  
  // Active call state
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [showNudge, setShowNudge] = useState(false);
  const timerRef = useRef(null);
  
  // Post-call notes form
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesForm, setNotesForm] = useState({
    call_outcome: 'completed',
    duration_seconds: 0,
    mood_rating: 3,
    engagement_level: 'medium',
    topics_discussed: [],
    notes: '',
    follow_up_needed: false,
    follow_up_notes: ''
  });
  const [topicInput, setTopicInput] = useState('');

  const getToken = () => localStorage.getItem('observer_token');

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/scheduling/observer/${observerId}/daily-queue?token=${token}`
      );
      setQueue(response.data.queue || []);
      setStats(response.data.stats || {});
      setIncompleteCall(response.data.incomplete_call);
      setTodayDate(response.data.date);
      
      // Check if there's an active call
      const inCallItem = response.data.queue?.find(q => q.call_status === 'in_call');
      if (inCallItem) {
        setActiveCall(inCallItem);
        // Start timer from when call started
        if (inCallItem.call_started_at) {
          const startTime = new Date(inCallItem.call_started_at);
          const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
          setCallDuration(elapsed);
          startTimer(elapsed);
        }
      }
      
      // Check if there's a call needing notes
      const needsNotes = response.data.queue?.find(
        q => q.call_status === 'call_ended' && !q.post_call_notes_submitted
      );
      if (needsNotes) {
        setActiveCall(needsNotes);
        setShowNotesForm(true);
        setNotesForm(prev => ({
          ...prev,
          duration_seconds: needsNotes.call_duration || 0
        }));
      }
    } catch (err) {
      console.error('Error loading queue:', err);
      toast({
        title: "Error",
        description: "Failed to load call queue",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [observerId, toast]);

  useEffect(() => {
    if (observerId) {
      loadQueue();
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [observerId, loadQueue]);

  const startTimer = (initialSeconds = 0) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setCallDuration(initialSeconds);
    
    timerRef.current = setInterval(() => {
      setCallDuration(prev => {
        const newDuration = prev + 1;
        // Show nudge at 4 minutes
        if (newDuration === NUDGE_TIME) {
          setShowNudge(true);
          // Play notification sound
          try {
            const audio = new Audio('/notification.mp3');
            audio.play().catch(() => {});
          } catch (e) {}
        }
        return newDuration;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startCall = async (item) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/observer/start-call/${item.assignment_id}?observer_id=${observerId}&token=${token}`
      );
      
      if (response.data.success) {
        toast({
          title: "Call Started",
          description: `Calling ${item.child_name}...`,
        });
        
        setActiveCall(item);
        setShowNudge(false);
        startTimer();
        loadQueue();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to start call",
        variant: "destructive"
      });
    }
  };

  const endCall = async () => {
    if (!activeCall) return;
    
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/observer/end-call/${activeCall.assignment_id}?observer_id=${observerId}&token=${token}`
      );
      
      if (response.data.success) {
        stopTimer();
        setNotesForm(prev => ({
          ...prev,
          duration_seconds: response.data.duration_seconds || callDuration
        }));
        setShowNotesForm(true);
        setShowNudge(false);
        
        toast({
          title: "Call Ended",
          description: "Please complete the post-call notes",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to end call",
        variant: "destructive"
      });
    }
  };

  const submitNotes = async () => {
    if (!notesForm.notes.trim()) {
      toast({
        title: "Notes Required",
        description: "Please add notes about the call",
        variant: "destructive"
      });
      return;
    }
    
    setNotesLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/observer/post-call-notes/${activeCall.assignment_id}?observer_id=${observerId}&token=${token}`,
        notesForm
      );
      
      if (response.data.success) {
        toast({
          title: "Notes Submitted",
          description: "Next call is now unlocked",
        });
        
        setShowNotesForm(false);
        setActiveCall(null);
        setCallDuration(0);
        setNotesForm({
          call_outcome: 'completed',
          duration_seconds: 0,
          mood_rating: 3,
          engagement_level: 'medium',
          topics_discussed: [],
          notes: '',
          follow_up_needed: false,
          follow_up_notes: ''
        });
        
        loadQueue();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to submit notes",
        variant: "destructive"
      });
    } finally {
      setNotesLoading(false);
    }
  };

  const addTopic = () => {
    if (topicInput.trim()) {
      setNotesForm(prev => ({
        ...prev,
        topics_discussed: [...prev.topics_discussed, topicInput.trim()]
      }));
      setTopicInput('');
    }
  };

  const removeTopic = (index) => {
    setNotesForm(prev => ({
      ...prev,
      topics_discussed: prev.topics_discussed.filter((_, i) => i !== index)
    }));
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-gray-100 text-gray-600';
      case 'in_call': return 'bg-blue-100 text-blue-700';
      case 'call_ended': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* 4-Minute Nudge Alert */}
      {showNudge && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <Card className="bg-orange-500 text-white border-0 shadow-lg">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">4 Minutes!</p>
                <p className="text-sm opacity-90">Consider wrapping up the call</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-orange-600"
                onClick={() => setShowNudge(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Call Timer */}
      {activeCall && !showNotesForm && (
        <Card className="border-2 border-green-500 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                  <PhoneCall className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">In Call With</p>
                  <p className="text-2xl font-bold text-green-800">{activeCall.child_name}</p>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-green-600">Duration</p>
                <p className={`text-4xl font-mono font-bold ${callDuration >= NUDGE_TIME ? 'text-orange-600' : 'text-green-800'}`}>
                  {formatDuration(callDuration)}
                </p>
                {callDuration >= NUDGE_TIME && (
                  <p className="text-xs text-orange-600 mt-1">Past recommended time</p>
                )}
              </div>
              
              <Button 
                onClick={endCall}
                className="bg-red-600 hover:bg-red-700 text-white h-14 px-8"
              >
                <Square className="w-5 h-5 mr-2" />
                End Call
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Post-Call Notes Form */}
      {showNotesForm && activeCall && (
        <Card className="border-2 border-yellow-500 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <MessageSquare className="w-5 h-5" />
              Post-Call Notes - {activeCall.child_name}
            </CardTitle>
            <CardDescription>
              Complete these notes to unlock your next call
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Call Outcome */}
              <div>
                <Label>Call Outcome *</Label>
                <select
                  value={notesForm.call_outcome}
                  onChange={(e) => setNotesForm({...notesForm, call_outcome: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value="completed">Completed Successfully</option>
                  <option value="no_answer">No Answer</option>
                  <option value="rescheduled">Rescheduled</option>
                  <option value="cancelled">Cancelled by Parent</option>
                </select>
              </div>
              
              {/* Duration */}
              <div>
                <Label>Duration</Label>
                <div className="mt-1 h-10 px-3 border rounded-md bg-gray-50 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {formatDuration(notesForm.duration_seconds)}
                </div>
              </div>
              
              {/* Engagement Level */}
              <div>
                <Label>Engagement Level</Label>
                <select
                  value={notesForm.engagement_level}
                  onChange={(e) => setNotesForm({...notesForm, engagement_level: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value="high">High - Very Engaged</option>
                  <option value="medium">Medium - Normally Engaged</option>
                  <option value="low">Low - Distracted/Quiet</option>
                </select>
              </div>
            </div>

            {/* Mood Rating */}
            <div>
              <Label>Child's Mood (1-5)</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setNotesForm({...notesForm, mood_rating: rating})}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all ${
                      notesForm.mood_rating === rating 
                        ? 'bg-yellow-500 text-white scale-110' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {rating === 1 ? '😢' : rating === 2 ? '😕' : rating === 3 ? '😐' : rating === 4 ? '🙂' : '😄'}
                  </button>
                ))}
              </div>
            </div>

            {/* Topics Discussed */}
            <div>
              <Label>Topics Discussed</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add a topic..."
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                />
                <Button type="button" onClick={addTopic} variant="outline">Add</Button>
              </div>
              {notesForm.topics_discussed.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {notesForm.topics_discussed.map((topic, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                    >
                      {topic}
                      <button onClick={() => removeTopic(idx)} className="hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Notes *</Label>
              <Textarea
                placeholder="Brief summary of the call, key observations, any concerns..."
                value={notesForm.notes}
                onChange={(e) => setNotesForm({...notesForm, notes: e.target.value})}
                className="mt-1"
                rows={4}
              />
            </div>

            {/* Follow-up */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="follow_up"
                checked={notesForm.follow_up_needed}
                onChange={(e) => setNotesForm({...notesForm, follow_up_needed: e.target.checked})}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="follow_up">Follow-up Needed</Label>
                {notesForm.follow_up_needed && (
                  <Textarea
                    placeholder="What needs follow-up?"
                    value={notesForm.follow_up_notes}
                    onChange={(e) => setNotesForm({...notesForm, follow_up_notes: e.target.value})}
                    className="mt-2"
                    rows={2}
                  />
                )}
              </div>
            </div>

            <Button 
              onClick={submitNotes}
              disabled={notesLoading || !notesForm.notes.trim()}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
            >
              {notesLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Notes & Unlock Next Call
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-slate-50">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-slate-700">{stats.total || 0}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-green-700">{stats.completed || 0}</p>
            <p className="text-xs text-green-600">Completed</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{stats.pending || 0}</p>
            <p className="text-xs text-blue-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-purple-700">{stats.in_call || 0}</p>
            <p className="text-xs text-purple-600">In Call</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-yellow-700">{stats.needs_notes || 0}</p>
            <p className="text-xs text-yellow-600">Needs Notes</p>
          </CardContent>
        </Card>
      </div>

      {/* Call Queue */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg">Today's Calls - {todayDate}</CardTitle>
            <CardDescription>Your daily call queue</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadQueue} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Phone className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No calls scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {queue.map((item, idx) => (
                <div 
                  key={item.assignment_id || idx}
                  className={`p-4 rounded-lg border-2 ${
                    item.is_locked 
                      ? 'bg-gray-50 border-gray-200 opacity-60' 
                      : item.call_status === 'in_call'
                        ? 'bg-green-50 border-green-300'
                        : item.call_status === 'completed'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="text-lg font-mono font-bold text-slate-700">{item.scheduled_time}</p>
                        <p className="text-xs text-slate-500">{item.duration_minutes}min</p>
                      </div>
                      
                      <div>
                        <p className="font-semibold text-slate-800">{item.child_name}</p>
                        {item.is_substitute && (
                          <p className="text-xs text-purple-600">Substitute assignment</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.call_status)}`}>
                        {item.call_status === 'call_ended' && !item.post_call_notes_submitted 
                          ? 'Needs Notes' 
                          : item.call_status}
                      </span>
                      
                      {item.is_locked ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm">Locked</span>
                        </div>
                      ) : item.call_status === 'pending' ? (
                        <Button
                          onClick={() => startCall(item)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={activeCall !== null}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start Call
                        </Button>
                      ) : item.call_status === 'completed' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : null}
                    </div>
                  </div>
                  
                  {item.is_locked && item.lock_reason && (
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {item.lock_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ObserverCallQueue;
